<template lang="pug">
.follower-send-message-history-tab
  .mb-3
  SendMessageHistoryTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onFileConfirm="filePreview"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="send-message-history-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・タイトル
          div ・内容
    // フィルタリング
    .d-flex.flex-row.align-items-center
      span.table-option-text 記載者:
      .filter
        b-form-select(
          id="filter-input"
          v-model="filter.writerId"
          :options="filterOptions"
          size="md"
        )
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )

  //- modals
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  SendMessageFilePreviewModal(ref="filePreviewModal")
</template>

<script lang="ts">
import { Component, Inject, Watch, Ref, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";
import Setting from "../../../../public/setting.json";

import FollowerMessagesService, { FollowerMessagesGetModel, File as UploadFile } from "@/commons/services/FollowerMessages";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import SendMessageHistoryTable, { SendMessageHistoryTableField, SendMessageHistoryTableItem, FilterSetting } from "@/pc/containers/followers/SendMessageHistoryTable.vue";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";

/**
 * ### フォロワーメッセージ履歴タブ
 */
@Component({
  components: {
    PageLoading,
    HelpIcon,
    SendMessageHistoryTable,
    SendMessageFilePreviewModal
  }
})
export default class SendMessageHistoryTab extends Vue {
  /** フォロワーメッセージサービス */
  @Inject("FollowerMessagesService") followerMessagesService!: FollowerMessagesService;
  /** アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ファイルプレビューモーダル */
  @Ref() filePreviewModal!: SendMessageFilePreviewModal;

  /** ローディング表示可否 */
  isLoading: boolean = false;
  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** テーブル列 */
  fields: Array<SendMessageHistoryTableField> = [];
  /** テーブル行 */
  items: Array<SendMessageHistoryTableItem> = [];
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", writerId: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.followers.sendMessages.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.followers.sendMessages.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.followers.sendMessages.pageRange;
  /** バックグラウンド取得するページ範囲 */
  bgPageRange: number = Setting.followers.sendMessages.bgPageRange;

  /**
   * ### 画面のリフレッシュ
   */
  public async refresh(): Promise<void> {
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `follower-send-message-history-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendMessageHistoryTableField> = this.createMessageFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.followers.sendMessages.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    // 記載者フィルタリング用のリストを作る
    const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(accounts);
    // フィルタの復元
    this.restoreFilterSetting();
    this.filterOptions = filterOptions;

    await this.createView();

    return;
  }

  /**
   * ### メッセージ履歴の列名を作成する
   */
  public createMessageFields(): Array<SendMessageHistoryTableField> {
    let fields: Array<SendMessageHistoryTableField> = [];

    fields.push({ key: "datetime", label: "日時", sortable: false, sortDirection: "desc", class: "datetime text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "text", label: "内容", sortable: false, sortDirection: "desc", class: "text" });
    fields.push({ key: "files", label: "添付ファイル", sortable: false, sortDirection: "desc", class: "files text-center" });
    fields.push({ key: "sendCount", label: "送信人数", sortable: false, sortDirection: "desc", class: "send-count text-center" });
    fields.push({ key: "userName", label: "記載者", sortable: false, sortDirection: "desc", class: "user-name" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["follower-send-message-history-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### アカウントを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const params: any = {
      all: "true"
    };
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore, params);
    return response;
  }

  /**
   * ### 記載者グループフィルター用選択項目
   */
  public createFilterOptions(accounts: Array<StandardAccountsGetModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    for (const account of accounts) {
      filterOptions.push({
        value: `${account.userId}`,
        text: account.name
      });
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    const totalRows: number = await this.setRows();

    // 面会予約テーブルに渡す各プロパティを作成する
    const messages: Array<FollowerMessagesGetModel> = await this.getMessages(this.currentRows, this.perPage * (this.currentPage * this.pageRange));
    const items: Array<SendMessageHistoryTableItem> = this.createItems(messages);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isLoading = false;

    await this.$timerUtil.setTimeout(1000);

    // 画面表示後に全件取得する
    while (this.currentRows < totalRows) {
      const messages: Array<FollowerMessagesGetModel> = await this.getMessages(this.currentRows, this.currentRows + this.perPage * this.bgPageRange);
      const items: Array<SendMessageHistoryTableItem> = this.createItems(messages);
      this.items = this.items.concat(items);
      this.currentRows = this.items.length;
      console.log("get background messages by family. this.currentRows:", this.currentRows, "totalRows:", totalRows);
      this.rows = this.items.length;

      await this.$timerUtil.setTimeout(1000);
    }

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(): Promise<number> {
    const record: Array<FollowerMessagesGetModel> = await this.followerMessagesService.getsInFacility(userStore);
    return record.length;
  }

  /**
   * ### メッセージ履歴を取得するAPI呼び出し
   */
  public async getMessages(startIndex: number, endIndex: number): Promise<Array<FollowerMessagesGetModel>> {
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex
    };
    const messages: Array<FollowerMessagesGetModel> = await this.followerMessagesService.getsInFacility(userStore, params);

    return messages;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(messages: Array<FollowerMessagesGetModel>): Array<SendMessageHistoryTableItem> {
    let items: Array<SendMessageHistoryTableItem> = [];

    for (const message of messages) {
      items.push({
        ...message,
        sendCount: message.to.length
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### ファイルをプレビュー表示する
   */
  public async filePreview(item: SendMessageHistoryTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const files: Array<File> = await this.restorationS3Files(item.files);

    this.isBusy = false;

    this.filePreviewModal.show("", false, files);

    return;
  }

  /**
   * ### S3ファイルを復元する
   */
  public async restorationS3Files(files: Array<UploadFile>): Promise<Array<File>> {
    let s3Files: Array<File> = [];

    for (const file of files) {
      const s3File: File = await this.getS3File(file.url, file.name);
      s3Files.push(s3File);
    }

    return s3Files;
  }

  /**
   * ### S3に格納されているFileを取得する
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>
